import { useMemo, useState } from 'react';

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  GridView as GridViewIcon,
  LocalOffer as LocalOfferIcon,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon,
  SortByAlpha as SortByAlphaIcon,
  TableRows as TableRowsIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useNavigate, useParams } from 'react-router';

import { SkeletonLoader } from '@/components/common';
import { PageContainer, PageHeading } from '@/components/layout';
import { useGetApplicationQuery, useGetWorkspaceUsersQuery } from '@/features/apps/api/appsApi';
import type { Transformer } from '@/types';
import { logger } from '@/utils/logger';

import { useDeleteTransformerMutation, useGetTransformersQuery } from '../api/transformersApi';
import { PropertiesModal } from '../components/PropertiesModal';

type ViewMode = 'list' | 'grid';
type SortField = 'name' | 'lastModified' | 'createDate' | 'deployed';
type SortOrder = 'asc' | 'desc';

export const RecipesListPage = () => {
  const { appId } = useParams();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortField, setSortField] = useState<SortField>('lastModified');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [alphabeticalSort, setAlphabeticalSort] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRecipe, setSelectedRecipe] = useState<Transformer | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);
  const [tagFilterAnchor, setTagFilterAnchor] = useState<null | HTMLElement>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  // Get application to retrieve tenantId
  const { data: app } = useGetApplicationQuery(appId || '', {
    skip: !appId,
  });

  const {
    data: recipes = [],
    isLoading,
    error,
  } = useGetTransformersQuery(appId || '', {
    skip: !appId,
  });

  // Mutations
  const [deleteTransformer, { isLoading: isDeleting }] = useDeleteTransformerMutation();

  // Load workspace users for user ID to email lookup
  const { data: workspaceUsers = [] } = useGetWorkspaceUsersQuery(
    {
      tenantId: app?.tenantId || '',
      appId: appId || '',
    },
    {
      skip: !appId || !app?.tenantId,
    }
  );

  // Helper function to get user email from user ID.
  // Matches legacy dataProtect behavior (transformers.js:userDetails) — look up by
  // user ID in the workspace users list and return the username (email).
  const getUserEmail = (userId: number | undefined): string => {
    if (!userId) return '-';
    const user = workspaceUsers.find((u) => Number(u.userId) === Number(userId));
    return user?.username || user?.email || '-';
  };

  // Get unique tags for filter
  const uniqueTags = useMemo(() => {
    const tags = recipes
      .map((r) => r.environ)
      .filter((tag): tag is string => Boolean(tag))
      .filter((tag, index, self) => self.indexOf(tag) === index)
      .sort();
    return ['ALL', ...tags];
  }, [recipes]);

  // Filter recipes by search term and tag
  const filteredRecipes = useMemo(() => {
    let filtered = recipes.filter((recipe) =>
      recipe.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply tag filter
    if (selectedTag && selectedTag !== 'ALL') {
      filtered = filtered.filter((recipe) => recipe.environ === selectedTag);
    }

    // Apply alphabetical sort if enabled
    if (alphabeticalSort) {
      filtered = [...filtered].sort((a, b) => {
        return sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      });
    } else {
      // Apply column-based sorting
      filtered = [...filtered].sort((a, b) => {
        let aVal: string | number | boolean | null | undefined;
        let bVal: string | number | boolean | null | undefined;

        switch (sortField) {
          case 'name':
            aVal = a.name || '';
            bVal = b.name || '';
            break;
          case 'lastModified':
            aVal = new Date(a.lastModified || 0).getTime();
            bVal = new Date(b.lastModified || 0).getTime();
            break;
          case 'createDate':
            aVal = new Date(a.createdDate || 0).getTime();
            bVal = new Date(b.createdDate || 0).getTime();
            break;
          case 'deployed':
            aVal = a.deployed ? 1 : 0;
            bVal = b.deployed ? 1 : 0;
            break;
          default:
            return 0;
        }

        if (typeof aVal === 'string') {
          return sortOrder === 'asc'
            ? aVal.localeCompare(bVal as string)
            : (bVal as string).localeCompare(aVal);
        }

        return sortOrder === 'asc'
          ? (aVal as number) - (bVal as number)
          : (bVal as number) - (aVal as number);
      });
    }

    return filtered;
  }, [recipes, searchTerm, selectedTag, sortField, sortOrder, alphabeticalSort]);

  // Paginated recipes
  const paginatedRecipes = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRecipes.slice(start, start + rowsPerPage);
  }, [filteredRecipes, page, rowsPerPage]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setAlphabeticalSort(false);
  };

  const handleAlphabeticalSort = () => {
    setAlphabeticalSort(!alphabeticalSort);
    if (!alphabeticalSort) {
      setSortField('name');
      setSortOrder('asc');
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, recipe: Transformer) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRecipe(recipe);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    // Don't clear selectedRecipe here - let the modal/dialog handlers clear it
  };

  const handleProperties = () => {
    setPropertiesModalOpen(true);
    handleCloseMenu();
  };

  const handleCloseProperties = () => {
    setPropertiesModalOpen(false);
    setSelectedRecipe(null);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  const handleDeleteConfirm = async () => {
    if (selectedRecipe) {
      try {
        await deleteTransformer(selectedRecipe.baseId).unwrap();
        setDeleteDialogOpen(false);
        setSelectedRecipe(null);
      } catch (error) {
        logger.error('Failed to delete recipe:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRecipe(null);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      // Format: "MMM Do Y, hh:mm A" (e.g., "Jan 28th 2026, 07:35 PM")
      // Use 'do' for ordinal (1st, 2nd, 3rd), 'yyyy' for year, 'hh' for hours with leading zero
      const formatted = format(new Date(dateString), 'MMM do yyyy, hh:mm a');
      // Uppercase AM/PM
      return formatted.replace(/(am|pm)$/, (match) => match.toUpperCase());
    } catch {
      return '-';
    }
  };

  if (error) {
    return (
      <PageContainer>
        <Alert severity="error">Failed to load recipes.</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeading
        title="Recipes"
        subtitle="Manage and deploy your data transformation recipes"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/a/${appId}/tf/new`)}
          >
            New Recipe
          </Button>
        }
      />

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          sx={{
            flex: 1,
            minWidth: 200,
            maxWidth: 480,
            bgcolor: 'white',
            '& .MuiOutlinedInput-root': {
              bgcolor: 'white',
              height: '40px',
            },
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Tag Filter Dropdown */}
          <Tooltip title="Filter by tag">
            <Button
              variant="outlined"
              size="small"
              startIcon={<LocalOfferIcon />}
              onClick={(e) => setTagFilterAnchor(e.currentTarget)}
            >
              Tag
            </Button>
          </Tooltip>
          <Menu
            anchorEl={tagFilterAnchor}
            open={Boolean(tagFilterAnchor)}
            onClose={() => setTagFilterAnchor(null)}
          >
            <RadioGroup
              value={selectedTag || 'ALL'}
              onChange={(e) => {
                setSelectedTag(e.target.value === 'ALL' ? null : e.target.value);
                setTagFilterAnchor(null);
                setPage(0);
              }}
            >
              {uniqueTags.map((tag) => (
                <MenuItem key={tag} sx={{ px: 2 }}>
                  <FormControlLabel value={tag} control={<Radio size="small" />} label={tag} />
                </MenuItem>
              ))}
            </RadioGroup>
          </Menu>

          <Tooltip title={alphabeticalSort ? 'Disable A-Z sort' : 'Sort A-Z'}>
            <ToggleButton
              value="alphabetical"
              selected={alphabeticalSort}
              onChange={handleAlphabeticalSort}
              size="small"
            >
              <SortByAlphaIcon />
            </ToggleButton>
          </Tooltip>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            size="small"
          >
            <ToggleButton value="list">
              <TableRowsIcon />
            </ToggleButton>
            <ToggleButton value="grid">
              <GridViewIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ flex: 1 }} />

        <Typography variant="body2" color="text.secondary">
          Rows per page:
        </Typography>
        <Box sx={{ minWidth: 80 }}>
          <TablePagination
            component="div"
            count={filteredRecipes.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage=""
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{
              '.MuiTablePagination-toolbar': {
                minHeight: 'auto',
                pl: 0,
              },
              '.MuiTablePagination-selectLabel': {
                display: 'none',
              },
              '.MuiTablePagination-displayedRows': {
                m: 0,
              },
            }}
          />
        </Box>
      </Box>

      {/* Content */}
      {isLoading && <SkeletonLoader variant="list" count={5} height={56} />}
      {!isLoading && viewMode === 'list' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 1 }}>
                  <TableSortLabel
                    active={sortField === 'name' && !alphabeticalSort}
                    direction={sortField === 'name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <TableSortLabel
                    active={sortField === 'lastModified' && !alphabeticalSort}
                    direction={sortField === 'lastModified' ? sortOrder : 'desc'}
                    onClick={() => handleSort('lastModified')}
                  >
                    Last Modified
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <TableSortLabel
                    active={sortField === 'createDate' && !alphabeticalSort}
                    direction={sortField === 'createDate' ? sortOrder : 'desc'}
                    onClick={() => handleSort('createDate')}
                  >
                    Created Date
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ py: 1 }}>
                  <TableSortLabel
                    active={sortField === 'deployed' && !alphabeticalSort}
                    direction={sortField === 'deployed' ? sortOrder : 'desc'}
                    onClick={() => handleSort('deployed')}
                  >
                    Deployed
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ py: 1 }}>Modified By</TableCell>
                <TableCell sx={{ py: 1 }}>Tag</TableCell>
                <TableCell align="right" sx={{ py: 1 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRecipes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm ? 'No match' : 'No Recipes.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRecipes.map((recipe) => (
                  <TableRow
                    key={recipe.baseId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/a/${appId}/tf/${recipe.baseId}`)}
                  >
                    <TableCell
                      sx={{
                        textDecoration: 'underline',
                        color: '#337ab7',
                        fontWeight: 'bold',
                        py: 1,
                      }}
                    >
                      {recipe.name}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>{formatDate(recipe.lastModified)}</TableCell>
                    <TableCell sx={{ py: 1 }}>{formatDate(recipe.createdDate)}</TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      {recipe.deployed ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>{getUserEmail(recipe.lastModifiedBy)}</TableCell>
                    <TableCell sx={{ py: 1 }}>{recipe.environ || ''}</TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenMenu(e, recipe);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!isLoading && viewMode !== 'list' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 2,
          }}
        >
          {paginatedRecipes.map((recipe) => (
            <Paper
              key={recipe.baseId}
              sx={{
                p: 2,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                },
              }}
              onClick={() => navigate(`/a/${appId}/tf/${recipe.baseId}`)}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1,
                }}
              >
                <Typography variant="h6" noWrap>
                  {recipe.name}
                </Typography>
                <IconButton size="small" onClick={(e) => handleOpenMenu(e, recipe)}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Modified: {formatDate(recipe.lastModified)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                {recipe.environ && <Chip label={recipe.environ} size="small" variant="outlined" />}
                <Chip
                  label={recipe.deployed ? 'Deployed' : 'Not Deployed'}
                  size="small"
                  color={recipe.deployed ? 'success' : 'default'}
                  variant={recipe.deployed ? 'filled' : 'outlined'}
                />
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleProperties}>
          <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
          Edit Tag
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" color="error" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedRecipe?.name && <>This will delete "{selectedRecipe.name}"</>}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={handleDeleteCancel}>
            Back
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Properties Modal */}
      <PropertiesModal
        open={propertiesModalOpen}
        recipe={selectedRecipe}
        appId={appId || ''}
        onClose={handleCloseProperties}
      />
    </PageContainer>
  );
};
